import { c as _c } from "react/compiler-runtime";
import { useDropLinkPreviewToggleControl } from "@/components/waves/drops/useDropLinkPreviewToggleControl";
import DropPartMarkdownWithPropLogger from "@/components/drops/view/part/DropPartMarkdownWithPropLogger";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ApiDropMentionedUser } from "@/generated/models/ApiDropMentionedUser";
import type { ApiDropGroupMention } from "@/generated/models/ApiDropGroupMention";
import type { ApiDropPart } from "@/generated/models/ApiDropPart";
import type { ApiDropReferencedNFT } from "@/generated/models/ApiDropReferencedNFT";
import type { ApiMentionedWave } from "@/generated/models/ApiMentionedWave";
import type { ApiWaveMin } from "@/generated/models/ApiWaveMin";
import React from "react";
import { useAuth } from "@/components/auth/Auth";
import QuorumProposalCompactContent from "@/components/waves/quorum/QuorumProposalCompactContent";
import { parseQuorumProposalMarkdown } from "@/components/waves/quorum/quorumProposalMarkdown";
import type { DropContentPresentation } from "./dropContentPresentation";
import EditDropLexical from "./EditDropLexical";
import WaveDropQuoteWithDropId from "./WaveDropQuoteWithDropId";
import { ApiDropType } from "@/generated/models/ApiDropType";
import type { ApiWaveMinWithChatLinkSettings } from "@/helpers/waves/wave.helpers";
import { CHAT_LINK_RESTRICTION_MESSAGE, areHandlesEqual, isChatLinkRestrictionApplicable } from "@/helpers/waves/chat-link-restriction.helpers";
interface WaveDropPartContentMarkdownProps {
  readonly mentionedUsers: Array<ApiDropMentionedUser>;
  readonly mentionedGroups?: Array<ApiDropGroupMention> | undefined;
  readonly mentionedWaves: Array<ApiMentionedWave>;
  readonly referencedNfts: Array<ApiDropReferencedNFT>;
  readonly part: ApiDropPart;
  readonly wave: ApiWaveMin;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly isEditing?: boolean | undefined;
  readonly isSaving?: boolean | undefined;
  readonly onSave?: ((newContent: string, mentions?: ApiDropMentionedUser[], mentionedGroups?: ApiDropGroupMention[], mentionedWaves?: ApiMentionedWave[]) => void) | undefined;
  readonly onCancel?: (() => void) | undefined;
  readonly drop?: ApiDrop | undefined; // Add drop to check for edited status
  readonly onLinkCardActionsActiveChange?: ((href: string, active: boolean) => void) | undefined;
  readonly contentPresentation?: DropContentPresentation | undefined;
  readonly embedPath?: readonly string[] | undefined;
  readonly quotePath?: readonly string[] | undefined;
  readonly embedDepth?: number | undefined;
  readonly maxEmbedDepth?: number | undefined;
  readonly fullWidthLinkPreviews?: boolean | undefined;
  readonly quorumCompactDetailsVisible?: boolean | undefined;
  readonly onQuorumCompactDetailsVisibleChange?: ((areDetailsVisible: boolean) => void) | undefined;
  readonly quorumCompactOpenSectionKeys?: readonly string[] | undefined;
  readonly onQuorumCompactSectionOpenChange?: ((sectionKey: string, isOpen: boolean) => void) | undefined;
}
const WaveDropPartContentMarkdown: React.FC<WaveDropPartContentMarkdownProps> = t0 => {
  const $ = _c(68);
  const {
    mentionedUsers,
    mentionedGroups: t1,
    mentionedWaves,
    referencedNfts,
    part,
    wave,
    onQuoteClick,
    isEditing: t2,
    isSaving: t3,
    onSave,
    onCancel,
    drop,
    onLinkCardActionsActiveChange,
    contentPresentation: t4,
    embedPath,
    quotePath,
    embedDepth,
    maxEmbedDepth,
    fullWidthLinkPreviews: t5,
    quorumCompactDetailsVisible,
    onQuorumCompactDetailsVisibleChange,
    quorumCompactOpenSectionKeys,
    onQuorumCompactSectionOpenChange
  } = t0;
  let t6;
  if ($[0] !== t1) {
    t6 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  const mentionedGroups = t6;
  const isEditing = t2 === undefined ? false : t2;
  const isSaving = t3 === undefined ? false : t3;
  const contentPresentation = t4 === undefined ? "default" : t4;
  const fullWidthLinkPreviews = t5 === undefined ? false : t5;
  const {
    connectedProfile
  } = useAuth();
  const linkPreviewToggleControl = useDropLinkPreviewToggleControl(drop);
  const dropId = drop?.id;
  const dropSerialNo = drop?.serial_no;
  const waveId = wave.id;
  let t7;
  bb0: {
    let t8;
    if ($[2] !== embedPath) {
      t8 = embedPath ? [...embedPath] : [];
      $[2] = embedPath;
      $[3] = t8;
    } else {
      t8 = $[3];
    }
    const path = t8;
    if (!dropId || path.includes(dropId)) {
      t7 = path;
      break bb0;
    }
    let t9;
    if ($[4] !== dropId || $[5] !== path) {
      t9 = [...path, dropId];
      $[4] = dropId;
      $[5] = path;
      $[6] = t9;
    } else {
      t9 = $[6];
    }
    t7 = t9;
  }
  const currentDropEmbedPath = t7;
  let t8;
  if ($[7] !== dropSerialNo || $[8] !== quotePath || $[9] !== waveId) {
    bb1: {
      const path_0 = quotePath ? [...quotePath] : [];
      if (dropSerialNo === undefined) {
        t8 = path_0;
        break bb1;
      }
      const currentQuoteKey = `${waveId}:${dropSerialNo}`;
      if (!path_0.includes(currentQuoteKey)) {
        path_0.push(currentQuoteKey);
      }
      t8 = path_0;
    }
    $[7] = dropSerialNo;
    $[8] = quotePath;
    $[9] = waveId;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const currentQuotePath = t8;
  let t9;
  if ($[11] !== contentPresentation || $[12] !== part.content) {
    t9 = contentPresentation === "quorumCompact" ? parseQuorumProposalMarkdown(part.content) : null;
    $[11] = contentPresentation;
    $[12] = part.content;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const compactProposal = t9;
  const waveWithLinkSettings = drop?.wave as ApiWaveMinWithChatLinkSettings | undefined;
  const editLinkRestrictionApplies = isChatLinkRestrictionApplicable({
    dropType: drop?.drop_type ?? ApiDropType.Chat,
    linksDisabled: waveWithLinkSettings?.links_disabled === true,
    isWaveAdmin: drop?.wave.authenticated_user_admin === true,
    isWaveCreator: areHandlesEqual(connectedProfile?.handle, waveWithLinkSettings?.wave_author_handle)
  });
  if (isEditing) {
    const t10 = part.content ?? "";
    const t11 = drop?.wave.authenticated_user_admin === true;
    const t12 = editLinkRestrictionApplies ? CHAT_LINK_RESTRICTION_MESSAGE : null;
    let t13;
    if ($[14] !== onSave) {
      t13 = (content, mentions, groups, waves) => {
        if (onSave) {
          onSave(content, mentions, groups, waves);
        }
      };
      $[14] = onSave;
      $[15] = t13;
    } else {
      t13 = $[15];
    }
    let t14;
    if ($[16] !== onCancel) {
      t14 = () => {
        if (onCancel) {
          onCancel();
        }
      };
      $[16] = onCancel;
      $[17] = t14;
    } else {
      t14 = $[17];
    }
    let t15;
    if ($[18] !== isSaving || $[19] !== mentionedGroups || $[20] !== mentionedUsers || $[21] !== mentionedWaves || $[22] !== t10 || $[23] !== t11 || $[24] !== t12 || $[25] !== t13 || $[26] !== t14 || $[27] !== wave.id) {
      t15 = <EditDropLexical initialContent={t10} initialMentions={mentionedUsers} initialGroupMentions={mentionedGroups} initialWaveMentions={mentionedWaves} canMentionAll={t11} waveId={wave.id} isSaving={isSaving} linkRestrictionMessage={t12} onSave={t13} onCancel={t14} />;
      $[18] = isSaving;
      $[19] = mentionedGroups;
      $[20] = mentionedUsers;
      $[21] = mentionedWaves;
      $[22] = t10;
      $[23] = t11;
      $[24] = t12;
      $[25] = t13;
      $[26] = t14;
      $[27] = wave.id;
      $[28] = t15;
    } else {
      t15 = $[28];
    }
    return t15;
  }
  let t10;
  if ($[29] !== compactProposal || $[30] !== currentDropEmbedPath || $[31] !== currentQuotePath || $[32] !== drop?.hide_link_preview || $[33] !== drop?.id || $[34] !== drop?.nft_links || $[35] !== embedDepth || $[36] !== fullWidthLinkPreviews || $[37] !== linkPreviewToggleControl || $[38] !== maxEmbedDepth || $[39] !== mentionedGroups || $[40] !== mentionedUsers || $[41] !== mentionedWaves || $[42] !== onLinkCardActionsActiveChange || $[43] !== onQuorumCompactDetailsVisibleChange || $[44] !== onQuorumCompactSectionOpenChange || $[45] !== onQuoteClick || $[46] !== part.content || $[47] !== quorumCompactDetailsVisible || $[48] !== quorumCompactOpenSectionKeys || $[49] !== referencedNfts) {
    t10 = compactProposal ? <QuorumProposalCompactContent proposal={compactProposal} mentionedUsers={mentionedUsers} mentionedGroups={mentionedGroups} mentionedWaves={mentionedWaves} referencedNfts={referencedNfts} nftLinks={drop?.nft_links} onQuoteClick={onQuoteClick} currentDropId={drop?.id} hideLinkPreviews={drop?.hide_link_preview} embedPath={currentDropEmbedPath} quotePath={currentQuotePath} embedDepth={embedDepth} maxEmbedDepth={maxEmbedDepth} fullWidthLinkPreviews={fullWidthLinkPreviews} linkPreviewToggleControl={linkPreviewToggleControl} onLinkCardActionsActiveChange={onLinkCardActionsActiveChange} areDetailsVisible={quorumCompactDetailsVisible} onDetailsVisibleChange={onQuorumCompactDetailsVisibleChange} openSectionKeys={quorumCompactOpenSectionKeys} onSectionOpenChange={onQuorumCompactSectionOpenChange} /> : <DropPartMarkdownWithPropLogger mentionedUsers={mentionedUsers} mentionedGroups={mentionedGroups} mentionedWaves={mentionedWaves} referencedNfts={referencedNfts} nftLinks={drop?.nft_links} partContent={part.content} onQuoteClick={onQuoteClick} currentDropId={drop?.id} hideLinkPreviews={drop?.hide_link_preview} embedPath={currentDropEmbedPath} quotePath={currentQuotePath} embedDepth={embedDepth} maxEmbedDepth={maxEmbedDepth} fullWidthLinkPreviews={fullWidthLinkPreviews} linkPreviewToggleControl={linkPreviewToggleControl} onLinkCardActionsActiveChange={onLinkCardActionsActiveChange} />;
    $[29] = compactProposal;
    $[30] = currentDropEmbedPath;
    $[31] = currentQuotePath;
    $[32] = drop?.hide_link_preview;
    $[33] = drop?.id;
    $[34] = drop?.nft_links;
    $[35] = embedDepth;
    $[36] = fullWidthLinkPreviews;
    $[37] = linkPreviewToggleControl;
    $[38] = maxEmbedDepth;
    $[39] = mentionedGroups;
    $[40] = mentionedUsers;
    $[41] = mentionedWaves;
    $[42] = onLinkCardActionsActiveChange;
    $[43] = onQuorumCompactDetailsVisibleChange;
    $[44] = onQuorumCompactSectionOpenChange;
    $[45] = onQuoteClick;
    $[46] = part.content;
    $[47] = quorumCompactDetailsVisible;
    $[48] = quorumCompactOpenSectionKeys;
    $[49] = referencedNfts;
    $[50] = t10;
  } else {
    t10 = $[50];
  }
  let t11;
  if ($[51] !== drop) {
    t11 = typeof drop?.updated_at === "number" && drop.updated_at !== drop.created_at && <div className="tw-mt-0.5 tw-text-[10px] tw-font-normal tw-leading-none tw-text-iron-500">(edited)</div>;
    $[51] = drop;
    $[52] = t11;
  } else {
    t11 = $[52];
  }
  let t12;
  if ($[53] !== t10 || $[54] !== t11) {
    t12 = <div>{t10}{t11}</div>;
    $[53] = t10;
    $[54] = t11;
    $[55] = t12;
  } else {
    t12 = $[55];
  }
  let t13;
  if ($[56] !== currentDropEmbedPath || $[57] !== currentQuotePath || $[58] !== embedDepth || $[59] !== maxEmbedDepth || $[60] !== onLinkCardActionsActiveChange || $[61] !== onQuoteClick || $[62] !== part.quoted_drop || $[63] !== wave) {
    t13 = part.quoted_drop?.drop_id && <div className="tw-mt-1.5"><WaveDropQuoteWithDropId dropId={part.quoted_drop.drop_id} partId={part.quoted_drop.drop_part_id} maybeDrop={part.quoted_drop.drop ? {
        ...part.quoted_drop.drop,
        wave
      } : null} waveId={wave.id} onQuoteClick={onQuoteClick} embedPath={currentDropEmbedPath} quotePath={currentQuotePath} embedDepth={(embedDepth ?? 0) + 1} maxEmbedDepth={maxEmbedDepth} onLinkCardActionsActiveChange={onLinkCardActionsActiveChange} /></div>;
    $[56] = currentDropEmbedPath;
    $[57] = currentQuotePath;
    $[58] = embedDepth;
    $[59] = maxEmbedDepth;
    $[60] = onLinkCardActionsActiveChange;
    $[61] = onQuoteClick;
    $[62] = part.quoted_drop;
    $[63] = wave;
    $[64] = t13;
  } else {
    t13 = $[64];
  }
  let t14;
  if ($[65] !== t12 || $[66] !== t13) {
    t14 = <>{t12}{t13}</>;
    $[65] = t12;
    $[66] = t13;
    $[67] = t14;
  } else {
    t14 = $[67];
  }
  return t14;
};
export default WaveDropPartContentMarkdown;